import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';

import TextInput from '../../components/ui/TextInput.jsx';
import CheckboxInput from '../../components/ui/CheckboxInput';
import Button from '../../components/ui/Button';
import { useAuth } from '../../context/AuthContext';
import { useHrAnalyticsApi, isRuleModuleSatisfied, missingModuleGroups, RULE_NAME_KEY } from './config';

// Görev 11 — HA Ayarları ve KVKK opt-in ekranı (spec §13.3 + §14). Rota/menü kaydı Görev 12'nin
// işi (brief) — bu dosya yalnız sayfayı kurar. Görsel dil HrAnalyticsAlertsPage.js/
// HrAnalyticsOverviewPage.js ile AYNI (Card + Title + Description), form yapısı
// TalentSettingsPage.js'in (spec'in kendisinin "TalentSettings deseni" dediği emsal) bölüm
// kartı + FooterBar + savedFlash kalıbı.
//
// ═══ Bu ekranın hukuki ağırlığı ═══ (bkz. task-11-brief.md, spec §14)
// IndividualScoringEnabled'ı açmak çalışan hakkında bireysel risk göstergesi üretmeye başlamak
// demektir. KVKK'da çalışan rızası güç asimetrisi nedeniyle geçerli bir hukuki sebep sayılmaz —
// bu yüzden bayrağı false'tan true'ya çeken TEK kod yolu (handleIndividualScoringToggle) local
// state'i DOĞRUDAN değiştirmez, meşru menfaat dengesi testi + aydınlatma metnini gösteren bir
// onay paneli AÇAR; yalnız o panelde "okudum, anladım" kutusu işaretlenip "Onayla ve Etkinleştir"
// tıklanırsa bayrak taslak (form) state'inde true olur. Kapatmak (true→false) bu kapıdan GEÇMEZ —
// güvenli yön olduğu için anında uygulanır. Onay paneli yalnız TASLAĞI değiştirir; sayfanın tek
// kalıcılaştırma noktası hâlâ aşağıdaki "Kaydet" butonudur (PUT tek bir bütün nesne alıyor,
// HaSettingsController — kısmi PATCH yok), böylece onay anında bekleyen BAŞKA taslak değişiklikler
// (ör. yarım yazılmış bir sayı alanı) sessizce kaydedilmez.
//
// ═══ MinCellSize / WeeklyHourThreshold tabanları ═══
// HaSettingsGuard.MinCellSizeFloor (5) ve WeeklyHourFloor (55) burada TEKRARLANIR (mirror) —
// kaynak sunucudur, sunucu sessizce kırpar (HaSettingsDataAccess.UpdateAsync). UI kırpmayı
// KENDİSİ yeniden UYGULAMAZ (kullanıcının yazdığını sessizce değiştirmez); bunun yerine kullanıcı
// tabanın altına indiğinde açık bir hata gösterir ve Kaydet'i devre dışı bırakır — "3'ün 5'e
// çekildiğini sonradan görmek" ile "girmenin baştan engellenmesi" arasındaki fark budur (brief).
//
// ═══ EnabledRuleCodesJson — üç durumlu ═══ (bkz. Görev 2 Adım 3, HaRuleCatalog.cs)
// null = tüm kurallar etkin (varsayılan/reset durumu); [] = kalıcı "hiçbiri etkin değil"; dolu
// liste = yalnız onlar. Yerel `ruleState = { isDefault, codes }` bu üçünü ayrı tutar:
// isDefault=true iken Kaydet `resetEnabledRuleCodes:true` gönderir (codes'a BAKILMAKSIZIN);
// isDefault=false iken `enabledRuleCodes: Array.from(codes)` gönderir — codes boşsa bu GEÇERLİ ve
// KALICI bir "hiçbiri etkin değil" isteğidir, "varsayılana dön" ile KARIŞTIRILMAZ. Tüm kutuları
// tek tek kapatmak (isDefault zaten false'a döner) ile "Varsayılana Dön" butonuna basmak
// (isDefault'u AÇIKÇA true'ya döndürür) bu yüzden ekranda ve giden istekte farklı sonuçlar üretir.
//
// ═══ Modülü olmayan kural — kilitli, gerekçeli, ASLA gizli değil ═══
// Kural kataloğu `GET /v1/ha/rules` ucundan gelir (FE aynası KALDIRILDI). Kilitli bir kural satırı
// devre dışı gösterilir ve GEREKÇESİYLE (eksik modül kodları) render edilir — hiçbir zaman
// listeden çıkarılmaz. Kilitli kural her zaman "işaretli" görünür: motoru gerçekten hiç
// koşmuyor (HaRuleCatalog.Resolve modül kapısını EnabledRuleCodesJson'dan ÖNCE kontrol eder),
// ama tenant modülü sonradan alırsa null=tüm-kurallar-etkin felsefesiyle TUTARLI biçimde otomatik
// etkin olsun diye Kaydet'te gönderilen listeye HER ZAMAN dahil edilir (kullanıcı onu hiç
// değiştiremez, dolayısıyla varsayılan "dahil" en az sürprizli sonuçtur).


// HaSettingsGuard.cs sabitlerinin BİREBİR aynası — bkz. dosya başı yorumu. Kaynak sunucudur,
// burada yalnız KOPYALANIR.
const MIN_CELL_SIZE_FLOOR = 5;
const MIN_CELL_SIZE_CEILING = 1000;
const WEEKLY_HOUR_FLOOR = 55;
const WEEKLY_HOUR_CEILING = 168;

// Kural kodları artık MODÜL DÜZEYİNDE SABİT DEĞİL: `GET /v1/ha/rules` ucundan geliyor
// (takip listesi md. 15). Eskiden bu dosya `HA_RULE_CATALOG` aynasından türetiyordu; elle
// tutulan bir liste, listeye yeni bir kural eklendiğinde tam gerektiği anda eksik kalıyordu.
// Bu yüzden "tüm kurallar açık" durumu artık ÖNCE kataloğun yüklenmesini bekler.
function deriveRuleState(data, allRuleCodes) {
  if (!data || data.allRulesEnabled) {
    return { isDefault: true, codes: new Set(allRuleCodes) };
  }
  return { isDefault: false, codes: new Set(data.enabledRuleCodes || []) };
}

function settingsToForm(data) {
  return {
    minCellSize: data.minCellSize,
    weeklyHourThreshold: data.weeklyHourThreshold,
    individualScoringEnabled: data.individualScoringEnabled,
    scoreVisibleToManager: data.scoreVisibleToManager,
    narrativeEnabled: data.narrativeEnabled,
    textThemeEnabled: data.textThemeEnabled,
  };
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PageDescription = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  max-width: 760px;
`;

const StateMessage = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const NoticeBanner = styled.div`
  padding: 12px 16px;
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  background: ${({ theme }) => `${theme.colors.auxiliar.info}18`};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  box-shadow: 0 1px 4px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.06);
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Description = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
`;

const FlagsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
`;

const HintText = styled.p`
  margin: 4px 0 0 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const CheckboxWrap = styled.div`
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;

const EnabledNotice = styled.div`
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  background: ${({ theme }) => `${theme.colors.auxiliar.warning}18`};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const WarningBanner = styled.div`
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  background: ${({ theme }) => `${theme.colors.auxiliar.danger}18`};
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const RuleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const RuleRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.border};

  &:last-child {
    border-bottom: none;
  }
`;

const RuleCodeCaption = styled.span`
  margin-left: 30px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const LockedReason = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 30px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

const ResetRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 4px;
`;

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  justify-content: flex-end;
`;

const SavedHint = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.auxiliar.success};
`;

// ═══ KVKK onay paneli — ConfirmDialog'un YENİDEN KULLANILAMADIĞI yer ═══
// AlertEvidenceDrawer.js'teki AYNI gerekçe: bu panelin içeriği ConfirmDialog'un basit
// title+message şeklinden çok daha zengin (denge testi + aydınlatma metni + AB YZ Yasası notu +
// onay kutusu), bu yüzden kendi Overlay/Dialog'unu kurar. Kaydırılabilir (`max-height`+
// `overflow-y`) çünkü içerik ConfirmDialog'un sabit 420px'inden UZUN.
const ConsentOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
`;

const ConsentDialog = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  padding: 28px;
  max-width: 640px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 16px 48px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.2);
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ConsentTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ConsentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ConsentSectionTitle = styled.h4`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ConsentBody = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  line-height: 1.5;
`;

const BalancingItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
`;

const BalancingLabel = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DisclosureTemplate = styled.blockquote`
  margin: 4px 0 0 0;
  padding: 10px 14px;
  border-left: 3px solid ${({ theme }) => theme.colors.primary.main};
  background: ${({ theme }) => `${theme.colors.primary.main}0c`};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-style: italic;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
`;

const AcknowledgeRow = styled.div`
  padding-top: 4px;
  border-top: 1px solid ${({ theme }) => theme.colors.surface.border};
`;

const ConsentActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

export default function HrAnalyticsSettingsPage() {
  const { t } = useTranslation();
  const { canWrite, hasModule } = useAuth();
  const { getSettings, updateSettings, getRules } = useHrAnalyticsApi();

  const writable = canWrite ? canWrite('HA') : false;
  const checkModule = useCallback((code) => (hasModule ? hasModule(code) : true), [hasModule]);

  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [form, setForm] = useState(null);
  // Kural kataloğu uçtan gelir; yüklenene kadar boş kalır (kod listesini UYDURMAYIZ).
  const [ruleCatalog, setRuleCatalog] = useState([]);
  const [ruleState, setRuleState] = useState({ isDefault: true, codes: new Set() });
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    // Hata toast'ı YOK (CLAUDE.md) — hatalar yalnız Axios interceptor'da ele alınır.
    //
    // İki istek BİRLİKTE bekleniyor: "tüm kurallar açık" durumunu kurmak için kural
    // KODLARININ bilinmesi şart. Ayarları önce uygulayıp kataloğu sonra beklemek, ilk
    // render'da tüm kutuları boş gösterip sonra doldurmak olurdu.
    Promise.all([getSettings(), getRules()])
      .then(([ayarRes, kuralRes]) => {
        if (cancelled) return;
        const kurallar = kuralRes?.data?.data ?? [];
        setRuleCatalog(kurallar);
        const data = ayarRes?.data?.data;
        if (data) {
          setForm(settingsToForm(data));
          setRuleState(deriveRuleState(data, kurallar.map((r) => r.code)));
        }
      })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [getSettings, getRules]);

  const setNumField = (key) => (e) => {
    const raw = e.target.value;
    setForm((prev) => ({ ...prev, [key]: raw === '' ? '' : Number(raw) }));
  };
  const setBoolField = (key) => (val) => setForm((prev) => ({ ...prev, [key]: val }));

  // Fix round 1 (koordinatör incelemesi): boş alan ÖNCEDEN hataya muaf tutuluyordu (yalnız
  // "< taban" kontrol ediliyordu) — bir backspace ile MinCellSize'ı temizlemek Kaydet'i etkin
  // BIRAKIYOR ve `{"minCellSize": ""}` gönderiyordu; DTO'nun alanı non-nullable `int` olduğu için
  // bu model binding'de HAM bir hataya düşüyordu, ekranın kendi "En az 5 olmalıdır." mesajına değil.
  // Boş dize artık AÇIKÇA geçersiz sayılır (taban kontrolünden ÖNCE, kısa devre ile) — "sunucunun
  // sessizce düzeltmesi" ile "UI'nin baştan engellemesi" arasındaki fark tam olarak bu ekranın
  // ispatlamak için var olduğu şey, temizlenmiş bir alan bu garantiyi atlatmamalı.
  const minCellSizeError = form !== null && (form.minCellSize === '' || Number(form.minCellSize) < MIN_CELL_SIZE_FLOOR)
    ? t('hrAnalytics.settings.minCellSize.error')
    : null;
  const weeklyHourError = form !== null && (form.weeklyHourThreshold === '' || Number(form.weeklyHourThreshold) < WEEKLY_HOUR_FLOOR)
    ? t('hrAnalytics.settings.weeklyHourThreshold.error')
    : null;

  // Bayrağı false→true çeken TEK yol — dosya başı yorumu. true→false ANINDA uygulanır (güvenli
  // yön), false→true onay panelini AÇAR ve taslağı DEĞİŞTİRMEZ; taslak yalnız
  // confirmIndividualScoring'de (onay kutusu işaretliyken) true olur.
  const handleIndividualScoringToggle = useCallback((next) => {
    if (next) {
      setConsentChecked(false);
      setConsentOpen(true);
      return;
    }
    setBoolField('individualScoringEnabled')(false);
  }, []);

  const confirmIndividualScoring = useCallback(() => {
    if (!consentChecked) return;
    setForm((prev) => ({ ...prev, individualScoringEnabled: true }));
    setConsentOpen(false);
  }, [consentChecked]);

  const cancelIndividualScoring = useCallback(() => {
    setConsentOpen(false);
  }, []);

  const toggleRule = useCallback((code, checked) => {
    setRuleState((prev) => {
      const nextCodes = new Set(prev.codes);
      if (checked) nextCodes.add(code); else nextCodes.delete(code);
      return { isDefault: false, codes: nextCodes };
    });
  }, []);

  const resetRules = useCallback(() => {
    setRuleState({ isDefault: true, codes: new Set(ruleCatalog.map((r) => r.code)) });
  }, [ruleCatalog]);

  const lockedCodes = ruleCatalog
    .filter((r) => !isRuleModuleSatisfied(r.requiredModules, checkModule))
    .map((r) => r.code);
  const availableCodes = ruleCatalog
    .map((r) => r.code)
    .filter((c) => !lockedCodes.includes(c));
  // Uyarı yalnız kullanıcının GERÇEKTEN kontrol edebildiği (kilitli olmayan) kuralların hepsi
  // kapalıyken gösterilir — kilitli bir kod geçmişten Set'te kalmış olsa bile (nadir: tenant
  // modülü kaybetti) bu uyarıyı YANLIŞLIKLA bastırmaz/tetiklemez.
  const noRulesEnabled = !ruleState.isDefault && availableCodes.every((c) => !ruleState.codes.has(c));

  const canSave = writable && form !== null && !minCellSizeError && !weeklyHourError && !saving && !fetching;

  const handleSave = useCallback(() => {
    if (!canSave) return;
    setSaving(true);
    // Kilitli kural kodları çıkan listeye HER ZAMAN dahil edilir — kullanıcı onları hiç
    // değiştiremez, null=tüm-kurallar-etkin varsayılan felsefesiyle tutarlı biçimde modül
    // sonradan gelirse otomatik etkin olsunlar diye (dosya başı yorumu).
    const explicitCodes = ruleState.isDefault
      ? null
      : Array.from(new Set([...ruleState.codes, ...lockedCodes]));

    const body = {
      minCellSize: form.minCellSize,
      weeklyHourThreshold: form.weeklyHourThreshold,
      individualScoringEnabled: form.individualScoringEnabled,
      scoreVisibleToManager: form.scoreVisibleToManager,
      narrativeEnabled: form.narrativeEnabled,
      textThemeEnabled: form.textThemeEnabled,
      enabledRuleCodes: explicitCodes,
      resetEnabledRuleCodes: ruleState.isDefault,
    };

    // No try/catch (HrAnalyticsAlertsPage.js'teki AYNI gerekçe) — Axios interceptor zaten
    // mutasyon hatalarını toast'lar; `finally` yalnız yerel `saving` durumunu temizler.
    updateSettings(body)
      .then((res) => {
        const data = res?.data?.data;
        if (data) {
          setForm(settingsToForm(data));
          setRuleState(deriveRuleState(data));
        }
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 2500);
      })
      .finally(() => setSaving(false));
  }, [canSave, form, ruleState, lockedCodes, updateSettings]);

  return (
    <Container>
      <PageTitle>{t('hrAnalytics.settings.pageTitle')}</PageTitle>
      <PageDescription>{t('hrAnalytics.settings.pageDescription')}</PageDescription>

      {fetching || !form ? (
        <StateMessage>{t('common.loading')}</StateMessage>
      ) : (
        <>
          {!writable && (
            <NoticeBanner data-role="ha-settings-readonly-notice">
              {t('hrAnalytics.settings.notWritableNotice')}
            </NoticeBanner>
          )}

          <Card>
            <SectionTitle>{t('hrAnalytics.settings.sections.privacy.title')}</SectionTitle>
            <FieldsGrid>
              <div>
                <TextInput
                  label={t('hrAnalytics.settings.minCellSize.label')}
                  type="number"
                  min={MIN_CELL_SIZE_FLOOR}
                  max={MIN_CELL_SIZE_CEILING}
                  data-field="minCellSize"
                  value={form.minCellSize}
                  onChange={setNumField('minCellSize')}
                  error={minCellSizeError}
                  disabled={!writable}
                />
                <HintText>{t('hrAnalytics.settings.minCellSize.hint')}</HintText>
              </div>
              <div>
                <TextInput
                  label={t('hrAnalytics.settings.weeklyHourThreshold.label')}
                  type="number"
                  min={WEEKLY_HOUR_FLOOR}
                  max={WEEKLY_HOUR_CEILING}
                  data-field="weeklyHourThreshold"
                  value={form.weeklyHourThreshold}
                  onChange={setNumField('weeklyHourThreshold')}
                  error={weeklyHourError}
                  disabled={!writable}
                />
                <HintText>{t('hrAnalytics.settings.weeklyHourThreshold.hint')}</HintText>
              </div>
            </FieldsGrid>
          </Card>

          <Card>
            <SectionTitle>{t('hrAnalytics.settings.sections.individualScoring.title')}</SectionTitle>
            <div>
              <CheckboxWrap $disabled={!writable} data-role="ha-field-individualScoringEnabled">
                <CheckboxInput
                  label={t('hrAnalytics.settings.individualScoringEnabled.label')}
                  checked={form.individualScoringEnabled}
                  onChange={writable ? handleIndividualScoringToggle : undefined}
                />
              </CheckboxWrap>
              <HintText>{t('hrAnalytics.settings.individualScoringEnabled.hint')}</HintText>
              {form.individualScoringEnabled && (
                <EnabledNotice data-role="ha-individual-scoring-enabled-notice">
                  {t('hrAnalytics.settings.individualScoringEnabled.enabledNotice')}
                </EnabledNotice>
              )}
            </div>

            <div>
              <CheckboxWrap
                $disabled={!writable || !form.individualScoringEnabled}
                data-role="ha-field-scoreVisibleToManager"
              >
                <CheckboxInput
                  label={t('hrAnalytics.settings.scoreVisibleToManager.label')}
                  checked={form.scoreVisibleToManager}
                  onChange={(writable && form.individualScoringEnabled) ? setBoolField('scoreVisibleToManager') : undefined}
                />
              </CheckboxWrap>
              <HintText>
                {form.individualScoringEnabled
                  ? t('hrAnalytics.settings.scoreVisibleToManager.hint')
                  : t('hrAnalytics.settings.scoreVisibleToManager.disabledHint')}
              </HintText>
            </div>
          </Card>

          <Card>
            <SectionTitle>{t('hrAnalytics.settings.sections.ai.title')}</SectionTitle>
            <FlagsGrid>
              <div>
                <CheckboxWrap $disabled={!writable} data-role="ha-field-narrativeEnabled">
                  <CheckboxInput
                    label={t('hrAnalytics.settings.narrativeEnabled.label')}
                    checked={form.narrativeEnabled}
                    onChange={writable ? setBoolField('narrativeEnabled') : undefined}
                  />
                </CheckboxWrap>
                <HintText>{t('hrAnalytics.settings.narrativeEnabled.hint')}</HintText>
              </div>
              <div>
                <CheckboxWrap $disabled={!writable} data-role="ha-field-textThemeEnabled">
                  <CheckboxInput
                    label={t('hrAnalytics.settings.textThemeEnabled.label')}
                    checked={form.textThemeEnabled}
                    onChange={writable ? setBoolField('textThemeEnabled') : undefined}
                  />
                </CheckboxWrap>
                <HintText>{t('hrAnalytics.settings.textThemeEnabled.hint')}</HintText>
              </div>
            </FlagsGrid>
          </Card>

          <Card>
            <SectionTitle>{t('hrAnalytics.settings.sections.rules.title')}</SectionTitle>
            <Description>{t('hrAnalytics.settings.sections.rules.description')}</Description>

            {noRulesEnabled && (
              <WarningBanner data-role="ha-rules-none-enabled-warning">
                {t('hrAnalytics.settings.rules.noneEnabledWarning')}
              </WarningBanner>
            )}

            <RuleList>
              {ruleCatalog.map(({ code, requiredModules }) => {
                const locked = lockedCodes.includes(code);
                const checked = locked || ruleState.codes.has(code);
                const missingText = locked
                  ? missingModuleGroups(requiredModules, checkModule).map((g) => g.join(' / ')).join(' + ')
                  : '';
                return (
                  <RuleRow key={code} data-role="ha-rule-row" data-code={code} data-locked={locked ? 'true' : 'false'}>
                    <CheckboxWrap $disabled={locked || !writable}>
                      <CheckboxInput
                        label={t(`hrAnalytics.settings.rules.names.${RULE_NAME_KEY[code]}`)}
                        checked={checked}
                        onChange={(!locked && writable) ? (next) => toggleRule(code, next) : undefined}
                      />
                    </CheckboxWrap>
                    <RuleCodeCaption>{code}</RuleCodeCaption>
                    {locked && (
                      <LockedReason data-role="ha-rule-locked-reason">
                        <Lock size={12} aria-hidden="true" />
                        {t('hrAnalytics.states.locked_module')} ({missingText})
                      </LockedReason>
                    )}
                  </RuleRow>
                );
              })}
            </RuleList>

            <ResetRow>
              <Button
                variant="outline"
                type="button"
                onClick={resetRules}
                disabled={!writable || ruleState.isDefault}
                data-role="ha-rules-reset"
              >
                {t('hrAnalytics.settings.rules.resetButton')}
              </Button>
              <HintText>{t('hrAnalytics.settings.rules.resetHint')}</HintText>
            </ResetRow>
          </Card>

          <FooterBar>
            {savedFlash && <SavedHint>{t('hrAnalytics.settings.savedHint')}</SavedHint>}
            {writable && (
              <Button
                type="button"
                onClick={handleSave}
                disabled={!canSave}
                loading={saving}
                data-role="ha-settings-save"
              >
                {saving ? t('common.saving') : t('common.save')}
              </Button>
            )}
          </FooterBar>
        </>
      )}

      {consentOpen && (
        <ConsentOverlay data-role="ha-individual-scoring-consent" onClick={cancelIndividualScoring}>
          <ConsentDialog onClick={(e) => e.stopPropagation()}>
            <ConsentTitle>{t('hrAnalytics.settings.consent.title')}</ConsentTitle>

            <ConsentSection>
              <ConsentSectionTitle>{t('hrAnalytics.settings.consent.whatChanges.title')}</ConsentSectionTitle>
              <ConsentBody>{t('hrAnalytics.settings.consent.whatChanges.body')}</ConsentBody>
            </ConsentSection>

            <ConsentSection>
              <ConsentSectionTitle>{t('hrAnalytics.settings.consent.legalBasis.title')}</ConsentSectionTitle>
              <ConsentBody>{t('hrAnalytics.settings.consent.legalBasis.body')}</ConsentBody>
            </ConsentSection>

            <ConsentSection>
              <ConsentSectionTitle>{t('hrAnalytics.settings.consent.balancingTestTitle')}</ConsentSectionTitle>
              <BalancingItem>
                <BalancingLabel>{t('hrAnalytics.settings.consent.balancing.purpose.title')}</BalancingLabel>
                <ConsentBody>{t('hrAnalytics.settings.consent.balancing.purpose.body')}</ConsentBody>
              </BalancingItem>
              <BalancingItem>
                <BalancingLabel>{t('hrAnalytics.settings.consent.balancing.necessity.title')}</BalancingLabel>
                <ConsentBody>{t('hrAnalytics.settings.consent.balancing.necessity.body')}</ConsentBody>
              </BalancingItem>
              <BalancingItem>
                <BalancingLabel>{t('hrAnalytics.settings.consent.balancing.proportionality.title')}</BalancingLabel>
                <ConsentBody>{t('hrAnalytics.settings.consent.balancing.proportionality.body')}</ConsentBody>
              </BalancingItem>
            </ConsentSection>

            <ConsentSection>
              <ConsentSectionTitle>{t('hrAnalytics.settings.consent.disclosure.title')}</ConsentSectionTitle>
              <ConsentBody>{t('hrAnalytics.settings.consent.disclosure.body')}</ConsentBody>
              <DisclosureTemplate>{t('hrAnalytics.settings.consent.disclosure.template')}</DisclosureTemplate>
            </ConsentSection>

            <ConsentSection>
              <ConsentSectionTitle>{t('hrAnalytics.settings.consent.aiAct.title')}</ConsentSectionTitle>
              <ConsentBody>{t('hrAnalytics.settings.consent.aiAct.body')}</ConsentBody>
            </ConsentSection>

            <AcknowledgeRow data-role="ha-consent-acknowledge">
              <CheckboxInput
                label={t('hrAnalytics.settings.consent.acknowledgeLabel')}
                checked={consentChecked}
                onChange={setConsentChecked}
              />
            </AcknowledgeRow>

            <ConsentActions>
              <Button type="button" variant="outline" onClick={cancelIndividualScoring}>
                {t('hrAnalytics.settings.consent.cancelButton')}
              </Button>
              <Button
                type="button"
                onClick={confirmIndividualScoring}
                disabled={!consentChecked}
                data-role="ha-consent-confirm"
              >
                {t('hrAnalytics.settings.consent.confirmButton')}
              </Button>
            </ConsentActions>
          </ConsentDialog>
        </ConsentOverlay>
      )}
    </Container>
  );
}
